import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { selectedQueueTickets, selectSearchSrParams } from 'features/queue/slice';
import { getQueryKeyFromParams, getNewRequestFromParams } from 'features/queue/grid/helpers';
import { postSearchSrRequest } from 'services/ticketService';
import { QUERY_STALE_TIME, QUERIES_KEYS } from 'constant';
import { convertClientColumnConfig } from 'features/queue/utils';
import useIsGridPaginationEnabled from 'features/queue/newGrid/hooks/useIsGridPaginationEnabled';

const useQueueData = () => {
    const queryClient = useQueryClient();
    const searchSrParams = useSelector(selectSearchSrParams);
    const selectedTickets = useSelector(selectedQueueTickets);
    const searchSrRequest = getNewRequestFromParams(searchSrParams, selectedTickets);
    const isGridPaginationEnabled = useIsGridPaginationEnabled();

    const getQueueDataResponse = (queueData) => ({
      ...queueData,
      columnConfiguration: queueData.columnConfiguration.map((col) => convertClientColumnConfig(col)),
    });

    const queryFn = async ({ signal }) => {
      const response = await postSearchSrRequest(searchSrRequest, signal);
      return response;
    };
  
    const queryKey = useMemo(() => getQueryKeyFromParams(searchSrParams), [searchSrParams]);
  
    const commonOptions = useMemo(() => ({
      staleTime: QUERY_STALE_TIME.MINUTE,
      cacheTime: QUERY_STALE_TIME.MEDIUM,
    }), []);

    const queueData = useQuery(queryKey, {
      queryFn,
      enabled: !!searchSrParams && Object.keys(searchSrParams).length > 0 && isGridPaginationEnabled,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: getQueueDataResponse,
      ...commonOptions,
    });

    const removeQueueData = useCallback(() => {
      queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.QUEUE_DATA] });
    }, [queryClient]);

    const fetchQueueData = useCallback(async (params = {}) => {
      const mergedParams = { ...searchSrParams, ...params };
      const specificQueryKey = getQueryKeyFromParams(params);
      const queueDataResponse = await queryClient.fetchQuery(
        specificQueryKey, 
        async ({ signal }) => {
          const request = getNewRequestFromParams(mergedParams, selectedTickets);
          const response = await postSearchSrRequest(request, signal);
          return response;
        },
        commonOptions
      );

      const result = getQueueDataResponse(queueDataResponse);

      const { startRow, endRow } = mergedParams;
      const { serviceRequests, countTotal } = result;
      if (serviceRequests.length > 0 && countTotal > endRow) {
        const nextPageParams = {
          ...mergedParams,
          startRow: endRow,
          endRow: endRow + (endRow - startRow),
        };
        const nextPageQueryKey = getQueryKeyFromParams(nextPageParams);
  
        queryClient.prefetchQuery(
          nextPageQueryKey,
          async ({ signal }) => {
            const request = getNewRequestFromParams(nextPageParams, selectedTickets);
            const response = await postSearchSrRequest(request, signal);
            return response;
          },
          commonOptions
        );
      }

      return result;
    }, [queryClient, selectedTickets, commonOptions, searchSrParams]);

    return {
      ...queueData,
      fetchQueueData,
      removeQueueData,
    };
  }

export default useQueueData;